import { createFileRoute } from "@tanstack/react-router";
import heroBg from "@/assets/heroes/colorado-geo-ai-hero.jpg";
import { ServicePage } from "@/components/ServicePage";
import {
  Bot,
  Sparkles,
  Search,
  FileText,
  ShieldCheck,
  BarChart3,
  Wrench,
  Truck,
  Home as HomeIcon,
  Hammer,
  Trees,
  Zap,
} from "lucide-react";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { GeoAiSections } from "@/components/geo-ai/GeoAiSections";

const OG_IMAGE = `https://coloradowebimpressions.com${ogImageFor({ service: "geo-ai" })}`;

const GEO_AI_SERVICE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "GEO AI Optimization",
  provider: "Colorado Web Impressions",
  areaServed: "Colorado",
  description:
    "Generative Engine Optimization service that gets Colorado businesses cited in ChatGPT, Google AI Overviews, Perplexity, and Gemini.",
};

export const Route = createFileRoute("/geo-ai")({
  head: () => ({
    meta: [
      {
        title:
          "GEO & AI Search Optimization for Colorado — Get Cited by ChatGPT (2026)",
      },
      {
        name: "description",
        content:
          "GEO and AI search optimization for Colorado businesses. Get cited by ChatGPT, Google AI Overviews, Perplexity & Gemini — the new SEO, done since 2024.",
      },
      {
        property: "og:title",
        content: "GEO & AI Search Optimization for Colorado — Get Cited by ChatGPT (2026)",
      },
      {
        property: "og:description",
        content:
          "Get cited by ChatGPT, Google AI Overviews, Perplexity & Gemini — the new way Colorado customers search.",
      },
      { property: "og:url", content: "https://coloradowebimpressions.com/geo-ai" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: "Get Cited by ChatGPT & Google AI — Colorado GEO",
        description: "The new SEO is AI search — and we already do it.",
      }),
    ],
    links: [
      { rel: "canonical", href: "https://coloradowebimpressions.com/geo-ai" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(GEO_AI_SERVICE_SCHEMA),
      },
    ],
  }),
  component: () => (
    <ServicePage
      heroImage={{ src: heroBg, alt: "Colorado GEO and AI search optimization workspace with Front Range mountains — Colorado Web Impressions" }}
      slug="geo-ai"
      serviceName="GEO & AI Search"
      pricingRoute="/pricing"
      eyebrow="GEO & AI Search Optimization"
      title="Get Your Colorado Business Cited by ChatGPT, Gemini & Google AI Overviews"
      goldWord="ChatGPT, Gemini & Google AI Overviews"
      description="Generative Engine Optimization (GEO) for Colorado businesses. We optimize your brand to be the answer AI search engines recommend — before your competitors figure it out."
      intro={{
        heading:
          "Search just changed forever. Most Colorado agencies haven't caught up.",
        body: [
          "Your customers are no longer just Googling. They're asking ChatGPT, Perplexity, Gemini, and Google's AI Overviews — and those tools quote a small handful of trusted sources. If your Colorado business isn't one of them, you're invisible to a fast-growing slice of high-intent buyers.",
          "GEO (Generative Engine Optimization) is the new playbook. We make your website, content, and digital footprint readable, citable, and trustworthy to the large language models already rewriting how people find local services in Colorado Springs, Denver, and across the Front Range.",
        ],
      }}
      benefits={[
        "Get cited in ChatGPT, Gemini, Perplexity & Claude answers",
        "Rank inside Google's AI Overviews & SGE results",
        "Structured data & schema built for AI crawlers",
        "Authoritative content the LLMs actually trust",
        "Brand-entity & knowledge-graph optimization",
        "Tracking for AI referral traffic & visibility",
      ]}
      features={[
        {
          icon: Bot,
          title: "AI Search Visibility",
          desc: "Get your Colorado business surfaced inside ChatGPT, Gemini, Perplexity & Claude answers.",
        },
        {
          icon: Sparkles,
          title: "Google AI Overviews",
          desc: "Optimize for Google's AI Overviews so you appear above traditional results.",
        },
        {
          icon: FileText,
          title: "LLM-Ready Content",
          desc: "Clear, factual, citation-worthy content structured the way AI models prefer to quote.",
        },
        {
          icon: ShieldCheck,
          title: "Entity & Schema SEO",
          desc: "Structured data and knowledge-graph signals telling AI exactly who and what you are.",
        },
        {
          icon: Search,
          title: "Citation Building",
          desc: "Earn trusted Colorado mentions across the sources LLMs actually train on and cite.",
        },
        {
          icon: BarChart3,
          title: "AI Traffic Tracking",
          desc: "See which AI tools are sending you traffic — and what they're saying about you.",
        },
      ]}
      frameworkHeading="The Colorado GEO Engine™"
      frameworkIntro="A 5-step framework to turn your business into a source AI engines actually cite."
      framework={[
        {
          number: "01",
          title: "Entity Foundation",
          desc: "Tighten your brand entity, NAP, and knowledge-graph signals so AI engines understand who and what you are.",
        },
        {
          number: "02",
          title: "LLM-Ready Content",
          desc: "Rewrite key pages into the clear, factual, well-structured formats LLMs prefer to quote.",
        },
        {
          number: "03",
          title: "Schema & Structured Data",
          desc: "FAQ, Service, LocalBusiness, and Article schema layered so AI crawlers can parse you cleanly.",
        },
        {
          number: "04",
          title: "Citation Sources",
          desc: "Earn mentions in the directories, publications, and Colorado-relevant sources LLMs actually trust.",
        },
        {
          number: "05",
          title: "AI Visibility Tracking",
          desc: "Monitor citations across ChatGPT, Gemini, Perplexity, and Google AI Overviews and refine monthly.",
        },
      ]}
      pricing={undefined}
      industries={[
        {
          icon: Wrench,
          name: "HVAC",
          desc: "Get recommended when Coloradans ask AI 'best HVAC company near me'.",
        },
        {
          icon: Truck,
          name: "Plumbing",
          desc: "Be the plumber AI tools cite for emergency-service questions.",
        },
        {
          icon: HomeIcon,
          name: "Roofing",
          desc: "Get cited for storm and hail damage queries across the Front Range.",
        },
        {
          icon: Hammer,
          name: "General Contractors",
          desc: "Show up in AI answers for remodel and build questions in Colorado.",
        },
        {
          icon: Trees,
          name: "Landscaping",
          desc: "Be referenced for design, install, and Colorado-climate landscaping advice.",
        },
        {
          icon: Zap,
          name: "Electrical",
          desc: "Get cited for emergency electrician and panel-upgrade questions.",
        },
      ]}
      faqs={[
        {
          q: "What is GEO AI optimization?",
          a: "GEO AI (Generative Engine Optimization) is the process of optimizing your website, content, and online presence so that AI platforms like ChatGPT, Google Gemini, Perplexity, and Google AI Overviews cite your business in their answers. It's the next evolution of SEO — instead of ranking on a search results page, you get recommended inside an AI conversation.",
        },
        {
          q: "How is GEO AI different from regular SEO?",
          a: "Traditional SEO focuses on ranking in Google's search results through keywords and backlinks. GEO AI focuses on being cited by AI systems through structured content, FAQ formatting, entity recognition, and schema markup. At Colorado Web Impressions, we do both — because your Colorado customers are searching both ways.",
        },
        {
          q: "Will my Colorado Springs business actually show up in ChatGPT?",
          a: "Yes — if your content is structured correctly. AI platforms pull from authoritative, well-structured sources. We format your web pages, Google Business Profile, and content specifically to be crawled and cited by LLMs like ChatGPT and Gemini.",
        },
        {
          q: "How long does GEO AI optimization take to work?",
          a: "Most clients start seeing AI citations within 60–120 days. GEO AI can actually work faster than traditional SEO because AI systems update more frequently than Google's index.",
        },
        {
          q: "Do I need GEO AI if I already do SEO?",
          a: "Yes. Traditional SEO and GEO AI target different places your customers search. A customer might Google \"Colorado Springs HVAC company\" AND ask ChatGPT \"who's the best HVAC company near me?\" — you want to show up in both.",
        },
        {
          q: "Which AI platforms does GEO AI optimization target?",
          a: "We optimize for ChatGPT, Google AI Overviews, Google Gemini, Perplexity, Microsoft Copilot, and Apple Intelligence — the platforms your Colorado customers are already using daily.",
        },
        {
          q: "How much does GEO AI optimization cost in Colorado?",
          a: "Our GEO AI service is included in our Growth Marketing package at $1,650/month alongside SEO, Google Maps, and social media. We don't offer it in isolation because it works best as part of an integrated strategy.",
        },
        {
          q: "Which Colorado businesses benefit most from GEO AI?",
          a: "Home service businesses (HVAC, plumbing, roofing, electrical, landscaping), contractors, law firms, dental practices, and any local business with competition. If someone can ask an AI \"who should I hire for X in Colorado Springs or Denver\" — you need GEO AI.",
        },
      ]}
      sectionsAfterPricing={<GeoAiSections />}
    />
  ),
});
